/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState, useRef } from "react";
import { Table, Alert, Button } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import { Form } from "antd";

import { HeaderForm } from "./HeaderForm";
import { Spinner } from "../../components/spinner/Spinner";
import { removeDuplicates } from "../../utils/utils";

import {
  useFetchOrders,
  useFilteredOrders,
  useFetchOrdersBy,
  useFilters,
} from "../../app/orders/mutations";

import { useLocation, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { columns } from "./constants";
import "../../styles/orders/ordenes.scss";

export const Orders = () => {
  const _useFilteredOrders = useFilteredOrders();
  const { selectedFilters, addFilter, removeFilter, resetFilters } =
    useFilters();
  const [, setStatusValue] = useState(null);
  const [form] = Form.useForm();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [queryParams, setQueryParams] = useState({});
  const hasInitialized = useRef(false);

  useEffect(() => {
    const prevPage = localStorage?.getItem("PREV_PAGE");
    if(prevPage && prevPage === 'ocurrences') {
      resetFilters();
    }
    localStorage.setItem("PREV_PAGE", 'orders');
    
    // Only restore search parameters if we don't have URL params and haven't initialized yet
    if (!hasInitialized.current && !searchParams.get('id_orden')) {
      const savedSearchParams = localStorage.getItem("ORDERS_LAST_SEARCH");
      if (savedSearchParams) {
        try {
          const parsedParams = JSON.parse(savedSearchParams);
          
          // Check if we already have cached data for these params
          const cacheKey = ["orders", parsedParams];
          const cachedData = queryClient.getQueryData(cacheKey);
          
          if (cachedData) {
            // We have cached data, set params but React Query will use cache
            setQueryParams(parsedParams);
          } else {
            // No cached data, set params and let React Query fetch
            setQueryParams(parsedParams);
          }
        } catch (e) {
          console.error("Error parsing saved search params:", e);
        }
      }
    }
    
    hasInitialized.current = true;
  }, [queryClient, searchParams]);

  // Handle URL parameters for auto-search
  useEffect(() => {
    const id_orden = searchParams.get('id_orden');
    
    if (id_orden) {
      // Auto-search when URL has id_orden parameter
      const urlParams = { id_orden };
      setQueryParams(urlParams);
      // Store this search for "Volver" functionality
      localStorage.setItem("ORDERS_LAST_SEARCH", JSON.stringify(urlParams));
    }
  }, [searchParams]);

  let {
    isFetching,
    isError,
    error,
    orders,
    refetch,
  } = useFetchOrders(queryParams);

  const handleSubmit = async (queries) => {
    setIsLoading(true);
    setQueryParams(queries);
    resetFilters();
    localStorage.removeItem('CURRENT_ORDER');
    
    // Store search parameters for "Volver" functionality (much smaller than full orders data)
    localStorage.setItem("ORDERS_LAST_SEARCH", JSON.stringify(queries));
    
    setTimeout(() => {
      refetch().finally(() => setIsLoading(false));
    }, 500);
  };

  let key =
    _useFilteredOrders.length === 0
      ? orders
      : _useFilteredOrders !== -1
      ? _useFilteredOrders
      : [];

  if (isError && error instanceof Error)
    return (
      <Alert
        message="Ocurrión un error"
        description={error.message}
        type="error"
        showIcon
      />
    );

  const clientsDocuments = orders && orders.length > 0 ? removeDuplicates(orders.map(
    (order) => order.cedula
  )) : [];

  const clientsOrders =  orders && orders.length > 0 ? removeDuplicates(orders.map(
    (order) => order.ordenCompra
  )) : [];

  const ENTREGAS = orders && orders.length > 0 ? removeDuplicates(orders.map(
    (order) => order.entrega
  )) : [];

  const FECHA_ENTREGAS = orders && orders.length > 0 ? removeDuplicates(orders.map(
    (order) => order.fechaEntrega
  )) : [];

  const STATUSES_FILTER = removeDuplicates(key.map((o) => o.estado)).map(
    (o) => {
      return {
        value: o.toUpperCase(),
        label: o.toUpperCase(),
      };
    }
  );

  const handleFilterClose = (filter) => {
    removeFilter(filter);
    useFetchOrdersBy();
  };

  const handleFilterReset = () => {
    resetFilters();
    form.setFieldValue("fechaInicioPromesa", null);
    useFetchOrdersBy();
  };

  return (
    <>
      <div className="orders">
        <div className="orders-header">
          <h2>Gestión Telefónica</h2>
        </div>
        <div className="orders-form">
          <HeaderForm
            onSubmit={handleSubmit}
            disabled={orders.length === 0 ? true : false}
            ordersIds={clientsOrders}
            documentsIds={clientsDocuments}
            statuses={STATUSES_FILTER}
            entregaIds={ENTREGAS}
            fechasEntregas={FECHA_ENTREGAS}
            form={form}
            isLoading={isLoading || isFetching}
          />
        </div>

        <div className="orders-filters">
          {selectedFilters && selectedFilters.length > 0 && (
            <Button id="clear-filters-btn" type="link" icon={<ClearOutlined />}  onClick={handleFilterReset}>
              Limpiar Filtros
            </Button>
          )}

          {selectedFilters?.map((filter, index) => (
            <div className="tag-closable">
              {filter.label}: <b>{filter.value}</b>
              <button onClick={() => handleFilterClose(filter)}>x</button>
            </div>
          ))}
        </div>

        <div className="orders-table">
          <div className="orders-table-content">
            {isFetching && <Spinner tip="Cargando, por favor espere..." />}
            {!isFetching && (
              <Table
                columns={columns}
                dataSource={key}
                locale={{ emptyText: "No hay datos correspondientes" }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
